import { useGateValue } from "@statsig/react-bindings";
import { cardListClass } from "@/components/threads/shared.tsx";
import type { FetchStatus } from "@tanstack/react-query";
import { Lightbulb, Plus } from "lucide-react";
import { useState } from "react";
import type { Memory } from "@/components/memories/types.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useMemories } from "@/shared/memory/useMemories.ts";
import { AddMemoryDialog } from "../../components/memories/AddMemoryDialog.tsx";
import { MemoryList } from "../../components/memories/MemoryList.tsx";
import { Button } from "../../components/ui/button.tsx";
import { panelDescriptionClass, SettingsPanelHeader } from "./SettingsPanelHeader.tsx";

type MemoriesState = "list" | "empty" | "loading" | "unreachable";

/**
 * Which of the panel's four bodies to render. Mirrors `resolveAuthGate`: any
 * data we already hold wins, so an offline refetch keeps showing the memories
 * rather than replacing them with an error.
 *
 * The two failure states matter because their absence is the bug this panel was
 * reported for — a query that is paused (offline, no cache) stays `isPending`
 * forever, and one that errored reports neither pending nor data. Left to fall
 * through, both render "No memories yet" or an endless skeleton, telling a user
 * with hundreds of memories something untrue.
 */
export const resolveMemoriesState = ({
  memories,
  isPending,
  fetchStatus,
}: {
  memories: Memory[] | undefined;
  isPending: boolean;
  fetchStatus: FetchStatus;
}): MemoriesState => {
  if (memories !== undefined) return memories.length > 0 ? "list" : "empty";
  if (fetchStatus === "paused") return "unreachable";
  if (isPending) return "loading";
  // Not pending and still no data: the fetch failed.
  return "unreachable";
};

/**
 * Stands in for the list while it loads. Without it the empty state renders,
 * telling a user with hundreds of memories that they have none.
 */
const MemoriesLoading = () => (
  <output className={cardListClass}>
    <div className="text-sm text-muted-foreground">Loading memories…</div>
    {[...Array(6)].map((_, i) => (
      <Skeleton key={i} className="h-16 w-full" />
    ))}
  </output>
);

const MemoriesUnreachable = ({ onRetry }: { onRetry: () => void }) => (
  <div className="text-center py-12 text-muted-foreground">
    <div className="text-lg font-medium mb-2">Couldn't load your memories</div>
    <div className="text-sm mb-4">Check your connection and try again.</div>
    <Button
      variant="outline"
      size="sm"
      onClick={onRetry}
      data-statsig-name="settings-memories-retry"
    >
      Retry
    </Button>
  </div>
);

const MemoriesEmpty = () => (
  <div className="text-center py-12 text-muted-foreground">
    <div className="text-lg font-medium mb-2">No memories yet</div>
    <div className="text-sm">
      Memories help your assistant remember important details about you.
    </div>
  </div>
);

/** The user has memories, but none of them match the current search. */
const MemoriesNoMatches = () => (
  <div className="text-center py-12 text-muted-foreground">
    <div className="text-lg font-medium mb-2">No memories match your search</div>
  </div>
);

/**
 * The user's own memories, rendered as a Settings tab. Mirrors the standalone
 * memories view's content (list, add dialog, and the "remember:" tip) minus the
 * page shell — Settings supplies the header and tab chrome.
 */
export const MemoriesPanel = () => {
  const isAdmin = useGateValue("internal-features");
  const { data: memories, isPending, fetchStatus, refetch } = useMemories();
  const [isAddMemoryOpen, setIsAddMemoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const state = resolveMemoriesState({ memories, isPending, fetchStatus });

  // Client-side: the full list is already loaded, so search just narrows it
  // by what the memory says and who it is about.
  const query = searchQuery.trim().toLowerCase();
  // Only read in the "list" state, where the fetch has resolved non-empty, so
  // the undefined fallback never reaches the render.
  const matches = (memories ?? []).filter(
    (memory) => !query || `${memory.content} ${memory.subject.name}`.toLowerCase().includes(query),
  );

  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title="Memories"
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: "Search memories",
          statsigName: "settings-memories-search",
        }}
        action={{
          label: "Add memory",
          icon: Plus,
          onClick: () => setIsAddMemoryOpen(true),
          statsigName: "settings-memories-add",
        }}
      />

      <p className={panelDescriptionClass}>
        What the assistant remembers about you and your preferences.
      </p>

      <AddMemoryDialog open={isAddMemoryOpen} onOpenChange={setIsAddMemoryOpen} />

      {state === "list" && matches.length > 0 ? (
        <MemoryList memories={matches} isAdmin={isAdmin} />
      ) : null}
      {state === "list" && matches.length === 0 ? <MemoriesNoMatches /> : null}
      {state === "empty" ? <MemoriesEmpty /> : null}
      {state === "loading" ? <MemoriesLoading /> : null}
      {state === "unreachable" ? <MemoriesUnreachable onRetry={() => void refetch()} /> : null}

      <div className="text-xs text-muted-foreground italic mt-4">
        <Lightbulb className="size-4 inline-block mr-1" />
        you can tell the assistant to remember things by putting&nbsp;
        <span className="font-semibold">"remember:"</span> at the start of your message
      </div>
    </div>
  );
};
